import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { api, getExtensionPage } from '.';

interface IIdentityContext {
	/** The profile of the user, if we know who they are. This does NOT mean they are authorized. */
	user?: UserProfile;

	/** Whether we know who the user is - a valid login session. This does NOT mean they are authorized. */
	getAccessToken: () => string | undefined;

	/** Indicate whether the user is authorized to access this system */
	authorized: boolean;
	ensureAuthenticated: () => Promise<boolean>;
}

export const IdentityContext = React.createContext<IIdentityContext | undefined>(undefined);

export function useIdentity(allowAnonymous?: boolean): IIdentityContext & {
	/** @obsolete Do not use this, provided for backwards compatability only. This value will never change in practice, use getAccessToken() instead */
	accessToken?: string;
} {
	const context = useContext(IdentityContext);
	if (!context) {
		if(allowAnonymous === true) return {} as any
		throw new Error('Identity Context is unavailable');
	}

	return { ...context, accessToken: context.getAccessToken() };
}

export type UserProfile = {
	/** The email address of the user */
	email: string;

	/** The user's name */
	name: string;

	/** The current tenant identifier */
	t?: string;

	/** Alternate tenants available to the user */
	ta?: string;

	role?: string[];
	/** The current user's identifier, a guid that will not change per identity. Will be the same across tenants. */
	sub: string;
};

export interface IRefreshTokenApiResponse {
	accessToken?: string;
	user?: UserProfile;
	ok: boolean;
	code?: number;
	redirect_url?: string;
	error?: Error;
}
export interface IIdentityContextProviderProps {
	initialToken: IRefreshTokenApiResponse;
	refreshToken?: () => Promise<IRefreshTokenApiResponse>;
}

export let CurrentIdentity: IRefreshTokenApiResponse;
export function updateIdentity(identity: IRefreshTokenApiResponse) {
	CurrentIdentity = identity;
	return identity;
}

export async function handleRefreshTokenResponse(
	result: IRefreshTokenApiResponse | undefined,
	history: ReturnType<typeof useNavigate>,
	force?: boolean
) {
	if (result?.redirect_url) {
		// we need to request this redirect as it will initialise the identity server login
		const p = getExtensionPage(window.location.pathname.split('/')[1]);
		if (!p.anonymous || force) {
			try {
				const res = await fetch(result.redirect_url, { redirect: 'error', headers: { 'x-redirect-as-content': '1' } });
				if (!res.ok) throw new Error(`soft-redirect to ${result.redirect_url} failed`);

				const path = await res.text();
				console.log('redirecting to login page', path);
				history(path);
			} catch (e) {
				console.log('error in handlerefresh', e);
				// if the redirect happens, we will just redirect directly
				window.location.href = result.redirect_url;
			}
		}
		// window.location.href = result.redirect_url;
		return false;
	}
	if (result?.accessToken) {
		Object.assign(CurrentIdentity, result);
		return true;
	}
}

export function IdentityContextProvider({ refreshToken, initialToken, children }: React.PropsWithChildren<IIdentityContextProviderProps>) {
	CurrentIdentity = initialToken;
	const errorCount = useRef(0);

	const history = useNavigate();

	const identity = CurrentIdentity;

	// if (!identity) {
	// 	return (
	// 		<div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
	// 			<div>
	// 				<Extension name="shell.splash-logo" />

	// 				<h1 style={{ textAlign: 'center', margin: 0, padding: '10px', fontFamily: 'Roboto, san-serif', color: 'white' }}>
	// 					Authenticating...
	// 				</h1>
	// 			</div>
	// 		</div>
	// 	);
	// }

	useEffect(() => {
		handleRefreshTokenResponse(initialToken, history);
	}, [initialToken]);

	async function ensureAuthenticated() {
		const result = await refreshToken?.();
		// an error could occur because of a network change (happens often with resuming from sleep)
		if (result?.error) {
			errorCount.current++;
			// we should actually look at the expiry of the token and upon failure to refresh where
			// the current access token is now expired we could do this. As it is we are assuming
			// 30 minutes have elapsed, and the default token life is 60 minutes, so this should be
			// a safe assumption
			if (errorCount.current > 3) {
				// 5 failures
				alert('There has been an error with your session and we need to reload');
				window.location.reload();
			}
			return false;
		}
		errorCount.current = 0;

		handleRefreshTokenResponse(result, history);

		//alert('Refresh failed - token might have expired. Should redirect but we weren\'t given a redirect url');
		return false;
	}

	// ensure we refresh the token regularly
	React.useEffect(() => {
		if (!refreshToken) return;
		// every 10 minutes we refresh the token
		const interval = setInterval(ensureAuthenticated, 1000 * 60 * 10);
		return () => clearInterval(interval);
	}, []);

	const id: IRefreshTokenApiResponse = typeof identity === 'boolean' ? { ok: false } : identity;

	return (
		<IdentityContext.Provider
			value={{
				...id,
				authorized: Boolean(id && id.user && !!id.user.t),
				ensureAuthenticated,
				getAccessToken: api.getAccessToken
			}}
		>
			{children}
		</IdentityContext.Provider>
	);
}
